import { useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DialogCommonFields } from './DialogCommonFields';
import { useCreatePunches } from './useCreatePunches';
import { CreatedBy, CreatePunchesResponse, DayDataContext, PunchType } from '../types';
import { UUID_RE } from '../urlParams';

interface SinglePunchInput {
  date: string;
  time: string;
  punchType: PunchType;
  createdBy: CreatedBy;
  requestedBy?: string;
  reason: string;
}

interface AddSinglePunchDialogProps {
  open: boolean;
  date: string;
  context?: DayDataContext;
  onClose: () => void;
  onCreate: (punch: SinglePunchInput) => Promise<CreatePunchesResponse>;
}

const PUNCH_TYPES: PunchType[] = ['entry', 'break_start', 'break_end', 'exit'];

export const AddSinglePunchDialog = ({ open, date, context, onClose, onCreate }: AddSinglePunchDialogProps) => {
  const [time, setTime] = useState('09:00');
  const [punchType, setPunchType] = useState<PunchType>('entry');
  const [createdBy, setCreatedBy] = useState<CreatedBy>('admin');
  const [requestedBy, setRequestedBy] = useState('');
  const [reason, setReason] = useState('');
  const requestedByInvalid = requestedBy !== '' && !UUID_RE.test(requestedBy);
  const { submitting, error, partial, submit } = useCreatePunches<SinglePunchInput>(onCreate, onClose);

  const doSubmit = () => submit({ date, time, punchType, createdBy, requestedBy: requestedBy || undefined, reason: createdBy === 'employee_realtime' ? '' : reason });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add punch</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Date" type="date" value={date} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Time" type="time" value={time} onChange={(e) => setTime(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Punch type" select value={punchType} onChange={(e) => setPunchType(e.target.value as PunchType)} size="small">
            {PUNCH_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <DialogCommonFields createdBy={createdBy} onCreatedByChange={setCreatedBy} reason={reason} onReasonChange={setReason} requestedBy={requestedBy} onRequestedByChange={setRequestedBy} context={context} />
          {error && <Alert severity="error">{error}</Alert>}
          {partial && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Created {partial.created.length}, failed {partial.failed.length}
              </Typography>
              {partial.failed.map((f, i) => (
                <Typography key={i} variant="body2" color="error">
                  {f.date} {f.time} {f.punchType}: {f.error}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {partial ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={submitting || requestedByInvalid} startIcon={submitting ? <CircularProgress size={16} /> : undefined} onClick={doSubmit}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddSinglePunchDialog;
